import { useMemo, useState, type KeyboardEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';

import { useAgentes, useClientes } from '../../shared/api/queries.ts';
import { normalizarBusca } from '../../shared/format.ts';
import type { Agente, Cliente } from '../../shared/api/tipos.ts';
import {
  BotaoInstalarClient,
  DialogoInstalador,
  InstalacoesDoCliente,
  ResumoDasInstalacoes,
} from '../agentes/InstalacoesDoCliente.tsx';
import { useSessao } from '../auth/sessao.tsx';
import { DialogoCliente } from './DialogoCliente.tsx';

/**
 * Clientes e as máquinas de cada um, na mesma tela.
 *
 * Eram duas, e a pergunta do dia a dia atravessava as duas: "o PDV do cliente
 * tal está mandando nota?". Aqui a linha do cliente já responde, e expandir
 * mostra cada instalação com o último sinal que ela deu.
 */
export function Clientes() {
  const { data, isPending, isError, error } = useClientes();
  const { usuario } = useSessao();

  // O contador vê a lista dos clientes dele e nada mais: cadastrar, editar e
  // acompanhar máquina é operação do escritório. Esconder é só para a tela não
  // oferecer o que não funciona — quem recusa é a rota da API.
  const daEquipe = usuario !== null && usuario.papel !== 'contador';
  const agentes = useAgentes(daEquipe);

  const [busca, setBusca] = useState('');
  // O que fica guardado é o id, não o cliente: o diálogo mostra o certificado
  // guardado, e um objeto congelado no clique continuaria mostrando o estado
  // de antes do upload depois de a lista já ter sido atualizada.
  const [idEmEdicao, setIdEmEdicao] = useState<string | null>(null);
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const [expandido, setExpandido] = useState<string | null>(null);
  // Fora da linha de propósito: dentro dela, cada clique no diálogo abriria
  // também a edição do cliente.
  const [instalando, setInstalando] = useState<Cliente | null>(null);

  const clientes = data?.itens;

  // Filtro local: a lista de clientes de um escritório cabe inteira na memória,
  // e filtrar aqui responde a cada tecla sem ida ao servidor. Se um dia passar
  // de alguns milhares, isto vira um parâmetro de busca na API.
  const visiveis = useMemo(() => {
    if (clientes === undefined) return [];

    const termo = normalizarBusca(busca);
    if (termo === '') return clientes;

    return clientes.filter((cliente) =>
      normalizarBusca(`${cliente.razaoSocial} ${cliente.cnpj}`).includes(termo),
    );
  }, [clientes, busca]);

  const agentesPorCliente = useMemo(() => {
    const mapa = new Map<string, Agente[]>();
    for (const agente of agentes.data?.itens ?? []) {
      const lista = mapa.get(agente.empresaId);
      if (lista === undefined) mapa.set(agente.empresaId, [agente]);
      else lista.push(agente);
    }
    return mapa;
  }, [agentes.data]);

  const emEdicao =
    idEmEdicao === null ? undefined : clientes?.find((item) => item.id === idEmEdicao);

  function abrirNovo() {
    setIdEmEdicao(null);
    setDialogoAberto(true);
  }

  function abrirEdicao(cliente: Cliente) {
    setIdEmEdicao(cliente.id);
    setDialogoAberto(true);
  }

  return (
    <Stack spacing={2}>
      <Card>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 1.5 }}>
          <Box sx={{ flexGrow: 1, minWidth: 180 }}>
            <Typography variant="h2">Clientes</Typography>
            <Typography variant="body2" color="text.secondary">
              {daEquipe
                ? 'Clique em um cliente para editar, ou na seta para ver as máquinas dele.'
                : 'Os clientes que a sua contabilidade atende.'}
            </Typography>
          </Box>

          <TextField
            size="small"
            placeholder="Buscar por razão social ou CNPJ"
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />

          {daEquipe && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>
              Novo cliente
            </Button>
          )}
        </Toolbar>

        <CardContent sx={{ pt: 0 }}>
          {isError ? (
            <Alert severity="error">
              Não foi possível carregar os clientes. {mensagemDoErro(error)}
            </Alert>
          ) : isPending ? (
            <TabelaEsqueleto />
          ) : visiveis.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              {clientes !== undefined && clientes.length === 0
                ? daEquipe
                  ? 'Nenhum cliente cadastrado ainda. Use "Novo cliente" para começar.'
                  : 'Nenhum cliente vinculado à sua contabilidade ainda.'
                : `Nenhum cliente encontrado para "${busca}".`}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {daEquipe && <TableCell sx={{ width: 48 }} />}
                    <TableCell>Razão social</TableCell>
                    <TableCell>CNPJ</TableCell>
                    {daEquipe && (
                      <>
                        <TableCell align="right">Clients</TableCell>
                        <TableCell align="right">Instalar</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiveis.map((cliente) => (
                    <LinhaCliente
                      key={cliente.id}
                      cliente={cliente}
                      agentes={agentesPorCliente.get(cliente.id) ?? []}
                      expandido={expandido === cliente.id}
                      daEquipe={daEquipe}
                      podeAdministrar={usuario?.papel === 'administrador'}
                      aoExpandir={() =>
                        setExpandido((atual) => (atual === cliente.id ? null : cliente.id))
                      }
                      aoInstalar={() => setInstalando(cliente)}
                      aoAbrir={() => abrirEdicao(cliente)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {!isPending && !isError && (
        <Typography variant="caption" color="text.secondary">
          {visiveis.length} de {clientes?.length ?? 0}{' '}
          {(clientes?.length ?? 0) === 1 ? 'cliente' : 'clientes'}
        </Typography>
      )}

      {/* Montados só para a equipe: o diálogo do instalador consulta
          `/agentes/instalador` ao abrir, e para o contador essa rota responde
          403 — a consulta sairia mesmo com o diálogo fechado. */}
      {daEquipe && (
        <>
          <DialogoCliente
            aberto={dialogoAberto}
            cliente={emEdicao}
            aoFechar={() => setDialogoAberto(false)}
          />

          <DialogoInstalador
            cliente={instalando}
            instalacoes={(agentesPorCliente.get(instalando?.id ?? '') ?? []).length}
            aoFechar={() => setInstalando(null)}
          />
        </>
      )}
    </Stack>
  );
}

function LinhaCliente({
  cliente,
  agentes,
  expandido,
  daEquipe,
  podeAdministrar,
  aoExpandir,
  aoInstalar,
  aoAbrir,
}: {
  cliente: Cliente;
  agentes: readonly Agente[];
  expandido: boolean;
  daEquipe: boolean;
  podeAdministrar: boolean;
  aoExpandir: () => void;
  aoInstalar: () => void;
  aoAbrir: () => void;
}) {
  // Para quem não edita, a linha é texto: deixá-la com cara de botão
  // prometeria uma tela de edição que a API recusa.
  const comoBotao = daEquipe
    ? {
        hover: true,
        onClick: aoAbrir,
        // A linha é o controle, então precisa ser alcançável e acionável pelo
        // teclado como um botão seria — clique do mouse sozinho deixaria a
        // tela inoperável para quem navega por Tab.
        tabIndex: 0,
        role: 'button',
        'aria-label': `Editar ${cliente.razaoSocial}`,
        onKeyDown: (evento: KeyboardEvent<HTMLTableRowElement>) => {
          if (evento.key === 'Enter' || evento.key === ' ') {
            evento.preventDefault();
            aoAbrir();
          }
        },
      }
    : {};

  return (
    <>
      <TableRow
        {...comoBotao}
        sx={{
          cursor: daEquipe ? 'pointer' : 'default',
          opacity: cliente.ativa ? 1 : 0.6,
          '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 },
          '& > *': { borderBottom: expandido ? 'none' : undefined },
        }}
      >
        {daEquipe && (
          <TableCell sx={{ width: 48 }}>
            <IconButton
              size="small"
              aria-label={`${expandido ? 'Esconder' : 'Ver'} as máquinas de ${cliente.razaoSocial}`}
              // A seta abre a gaveta; o clique não pode subir para a linha,
              // que abre a edição.
              onClick={(evento) => {
                evento.stopPropagation();
                aoExpandir();
              }}
            >
              {expandido ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )}
            </IconButton>
          </TableCell>
        )}

        <TableCell>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <span>{cliente.razaoSocial}</span>
            {/* Só o inativo ganha etiqueta: marcar o que é normal vira ruído. */}
            {!cliente.ativa && <Chip size="small" label="Inativo" variant="outlined" />}
          </Stack>
        </TableCell>

        {/* Tabular para os CNPJs alinharem dígito a dígito na coluna. */}
        <TableCell sx={{ fontVariantNumeric: 'tabular-nums', width: '1%', whiteSpace: 'nowrap' }}>
          {cliente.cnpjFormatado}
        </TableCell>

        {daEquipe && (
          <>
            <TableCell align="right" sx={{ width: '1%', whiteSpace: 'nowrap' }}>
              <ResumoDasInstalacoes agentes={agentes} />
            </TableCell>

            <TableCell align="right" sx={{ width: '1%', whiteSpace: 'nowrap' }}>
              <BotaoInstalarClient aoInstalar={aoInstalar} />
            </TableCell>
          </>
        )}
      </TableRow>

      {daEquipe && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0, borderBottom: expandido ? undefined : 'none' }}>
            <Collapse in={expandido} timeout="auto" unmountOnExit>
              <InstalacoesDoCliente agentes={agentes} podeAdministrar={podeAdministrar} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function TabelaEsqueleto() {
  return (
    <Stack spacing={1} sx={{ py: 1 }}>
      {Array.from({ length: 5 }, (_, indice) => (
        <Skeleton key={indice} variant="rounded" height={40} />
      ))}
    </Stack>
  );
}

function mensagemDoErro(erro: unknown): string {
  return erro instanceof Error ? erro.message : '';
}
